import React from 'react';
import { Badge } from '../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import { getResponseOptions } from './responseStatistics';

interface MultipleChoiceResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const MultipleChoiceResponse: React.FC<MultipleChoiceResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, type, answered_count } = question;
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Sort options by count (descending) for better visual hierarchy
    const sortedOptions = [...getResponseOptions(question)].sort((a, b) => b.count - a.count);
    
    // Find the maximum count for percentage calculations
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5999 18.5834C15.2023 18.5834 18.9333 14.8525 18.9333 10.2501C18.9333 5.64771 15.2023 1.91675 10.5999 1.91675C5.99756 1.91675 2.2666 5.64771 2.2666 10.2501C2.2666 14.8525 5.99756 18.5834 10.5999 18.5834Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                        >
                            {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Response Options */}
            <div className="space-y-4">
                {sortedOptions.map((option) => {
                    const percentage = answered_count > 0 ? ((option.count / answered_count) * 100).toFixed(0) : '0';
                    const barWidth = answered_count > 0 ? (option.count / answered_count) * 100 : 0;
                    
                    return (
                        <div key={option.option_id} className="space-y-2">
                            {/* Option Label */}
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                    {option.label}
                                </span>
                                
                                {/* Count and Percentage */}
                                <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                        {option.count}
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {percentage}%
                                    </span>
                                </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${barWidth}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* No responses message */}
            {sortedOptions.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            )}
        </div>
    );
};

export default MultipleChoiceResponse;
